import { useCallback, useEffect, useState } from 'react';

import { useCallLogTracksMutation } from 'services/appointments/appointments';

import { notifyError, notifyToast } from 'shared/Toast/Toast';

import { useZoomCall } from 'hooks';
import { useAnalytics } from 'hooks/useAnalytics';

import {
  CAMERA_RETRY_DELAY_MS,
  getVideoErrorMessage,
  isCameraStartingError
} from './controls.settings';

export const useControlsMedia = () => {
  const logEvent = useAnalytics();
  const [callLogTracks] = useCallLogTracksMutation();
  const {
    appointment,
    audioEnabled,
    cameraId,
    microphoneId,
    room,
    toggleAudioEnabled,
    toggleLoadingRoom,
    toggleVideoEnabled,
    videoEnabled
  } = useZoomCall();
  const [isVideoBusy, setIsVideoBusy] = useState(false);
  const stream = room?.getMediaStream();

  const startAudio = useCallback(() => {
    stream
      ?.startAudio({
        backgroundNoiseSuppression: true,
        microphoneId,
        mute: !audioEnabled
      })
      .catch((err: unknown) => err);
  }, [stream, microphoneId, audioEnabled]);

  const stopVideo = useCallback(() => stream?.stopVideo(), [stream]);

  const startVideo = useCallback(
    () => stream?.startVideo({ cameraId, originalRatio: true }),
    [stream, cameraId]
  );

  const handleCallLogTracks = useCallback(
    (audio: boolean, video: boolean) => {
      callLogTracks({
        appointmentId: appointment?._id || '',
        tracks: { audio: { enabled: audio }, video: { enabled: video } }
      }).unwrap();
    },
    [appointment?._id, callLogTracks]
  );

  const handleUpdatedAudioEnabled = useCallback(
    (nextEnabled?: boolean, logEventMessage?: string) => {
      logEventMessage && logEvent(logEventMessage);
      const targetEnabled = typeof nextEnabled === 'boolean' ? nextEnabled : !audioEnabled;
      (targetEnabled ? stream?.unmuteAudio() : stream?.muteAudio())
        ?.then(() => toggleAudioEnabled(targetEnabled))
        ?.catch(({ reason }) => notifyError(reason));
      handleCallLogTracks(targetEnabled, videoEnabled);
    },
    [audioEnabled, videoEnabled, stream, toggleAudioEnabled, handleCallLogTracks, logEvent]
  );

  const handleUpdatedVideoEnabled = useCallback(
    (nextEnabled?: boolean, logEventMessage?: string) => {
      if (isVideoBusy) {
        notifyToast('Camera operation in progress. Please wait.', { autoClose: 2000 });
        return;
      }
      logEventMessage && logEvent(logEventMessage);
      const targetEnabled = typeof nextEnabled === 'boolean' ? nextEnabled : !videoEnabled;
      setIsVideoBusy(true);
      const videoPromise = targetEnabled ? startVideo() : stopVideo();
      videoPromise
        ?.then(() => {
          toggleVideoEnabled(targetEnabled);
          handleCallLogTracks(audioEnabled, targetEnabled);
        })
        ?.catch(({ reason }) => notifyError(reason))
        ?.finally(() => setIsVideoBusy(false));
      if (!videoPromise) setIsVideoBusy(false);
    },
    [
      isVideoBusy,
      videoEnabled,
      audioEnabled,
      startVideo,
      stopVideo,
      toggleVideoEnabled,
      handleCallLogTracks,
      logEvent
    ]
  );

  const handleDevicePermissionChange = useCallback(
    (payload: { name: 'microphone' | 'camera'; state: 'denied' | 'granted' | 'prompt' }) => {
      const isDenied = payload.state === 'denied';
      const actionPromise =
        payload.name === 'camera'
          ? isDenied
            ? stopVideo()
            : startVideo()
          : isDenied
            ? stream?.stopAudio()
            : stream?.startAudio();

      actionPromise
        ?.then(() => {
          if (payload.name === 'camera') {
            toggleVideoEnabled(!isDenied);
          } else {
            toggleAudioEnabled(!isDenied);
          }
          if (isDenied) {
            notifyError(`Please allow ${payload.name} permission to continue.`);
          }
        })
        ?.catch(({ reason }) => notifyError(reason));
    },
    [startVideo, stopVideo, stream, toggleAudioEnabled, toggleVideoEnabled]
  );

  const initializeRoom = useCallback(async () => {
    if (videoEnabled) {
      try {
        await startVideo();
      } catch (error) {
        if (isCameraStartingError(error)) {
          await new Promise((resolve) => setTimeout(resolve, CAMERA_RETRY_DELAY_MS));
          try {
            await startVideo();
          } catch (retryError) {
            if (!isCameraStartingError(retryError)) {
              notifyError(getVideoErrorMessage(retryError));
            }
          }
        } else {
          notifyError(getVideoErrorMessage(error));
        }
      }
    }
    toggleLoadingRoom(false);
    startAudio();
  }, [videoEnabled, startVideo, startAudio, toggleLoadingRoom]);

  useEffect(() => {
    initializeRoom();
  }, []);

  useEffect(() => {
    room?.on('device-permission-change', handleDevicePermissionChange);
    return () => {
      room?.off('device-permission-change', handleDevicePermissionChange);
    };
  }, [room, handleDevicePermissionChange]);

  return {
    audioEnabled,
    handleUpdatedAudioEnabled,
    handleUpdatedVideoEnabled,
    isVideoBusy,
    videoEnabled
  };
};
